const ANON_KEY = "lmp_google_anon_session";
const ABANDON_MS = 25000;
const HEADER_TTL_MS = 120000;

const state = {
  current: null,
  pinnedHeaders: null,
  pinnedUntil: 0,
  driveGroupId: null,
};

let transport = null;
const mapSeen = new Map();

function randomId() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID().replace(/-/g, "").slice(0, 24);
  }
  return `id${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function createSearchInteractionId() {
  return randomId();
}

export function getAnonymousSessionId() {
  try {
    const existing = window.localStorage.getItem(ANON_KEY);
    if (existing && existing.length >= 8) return existing;
    const next = randomId();
    window.localStorage.setItem(ANON_KEY, next);
    return next;
  } catch (err) {
    return randomId();
  }
}

export function ensureDriveGroupId() {
  if (!state.driveGroupId) state.driveGroupId = randomId();
  return state.driveGroupId;
}

export function getCurrentDriveGroupId() {
  return state.driveGroupId || null;
}

export function buildUsageHeaders(extra = {}) {
  const headers = {
    "X-Anonymous-Session-Id": getAnonymousSessionId(),
    "X-Search-Interaction-Id": extra.searchInteractionId || randomId(),
    "X-Google-Feature": extra.feature || "server",
    "X-Google-Actor": extra.actorType || "customer",
  };
  const driveGroupId = extra.driveGroupId || state.driveGroupId;
  if (driveGroupId) headers["X-Drive-Group-Id"] = driveGroupId;
  return headers;
}

export function withGoogleUsageHeaders(headers, request) {
  state.pinnedHeaders = headers;
  state.pinnedUntil = Date.now() + HEADER_TTL_MS;
  return Promise.resolve()
    .then(request)
    .finally(() => {
      state.pinnedUntil = Date.now() + 60000;
    });
}

export function getActiveGoogleUsageHeaders() {
  const pinned = state.pinnedHeaders && state.pinnedUntil > Date.now() ? state.pinnedHeaders : null;
  const tracker = state.current;
  if (!tracker && !pinned) return null;
  return {
    ...(pinned || {}),
    ...(tracker ? tracker.headers() : {}),
  };
}

export function setGoogleUsageTransport(fn) {
  transport = fn;
}

function postUsage(body, keepalive) {
  if (typeof transport === "function") {
    return transport(body, { keepalive: Boolean(keepalive) });
  }
  if (typeof fetch !== "function") return undefined;
  return fetch("/api/analytics/google-api-usage", {
    method: "POST",
    credentials: "include",
    keepalive: Boolean(keepalive),
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => {});
}

export function createInteractionTracker(options = {}) {
  let searchInteractionId = options.searchInteractionId || randomId();
  const feature = options.feature || "nearby";
  const actorType = options.actorType || "customer";
  const driveGroupId = options.driveGroupId || null;
  let startedAt = Date.now();
  const counts = {
    autocompleteRequests: 0,
    placeDetailsRequests: 0,
    dynamicMapLoads: 0,
    savedAddressUsed: 0,
    gpsUsed: 0,
    recentAddressUsed: 0,
  };
  let timer = null;
  let closed = false;
  let lastSignature = "";
  let autocompleteQueries = [];

  function headers() {
    const next = {
      "X-Anonymous-Session-Id": getAnonymousSessionId(),
      "X-Search-Interaction-Id": searchInteractionId,
      "X-Google-Feature": feature,
      "X-Google-Actor": actorType,
    };
    if (driveGroupId) next["X-Drive-Group-Id"] = driveGroupId;
    return next;
  }

  function payload(completed) {
    return {
      searchInteractionId,
      anonymousSessionId: getAnonymousSessionId(),
      feature,
      actorType,
      driveGroupId,
      completed,
      durationMs: Date.now() - startedAt,
      autocompleteQueries: autocompleteQueries.slice(),
      ...counts,
    };
  }

  function flush(completed, keepalive) {
    const body = payload(completed);
    const signature = JSON.stringify(body);
    if (signature === lastSignature) return;
    const hasSignal =
      counts.autocompleteRequests ||
      counts.placeDetailsRequests ||
      counts.dynamicMapLoads ||
      counts.savedAddressUsed ||
      counts.gpsUsed ||
      counts.recentAddressUsed;
    if (!hasSignal) return;
    lastSignature = signature;
    postUsage(body, keepalive);
  }

  function armAbandon() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      abandon();
    }, ABANDON_MS);
  }

  function reopenIfClosed() {
    if (!closed) return;
    closed = false;
    lastSignature = "";
    searchInteractionId = randomId();
    startedAt = Date.now();
    counts.autocompleteRequests = 0;
    counts.placeDetailsRequests = 0;
    counts.dynamicMapLoads = 0;
    counts.savedAddressUsed = 0;
    counts.gpsUsed = 0;
    counts.recentAddressUsed = 0;
    autocompleteQueries = [];
    tracker.searchInteractionId = searchInteractionId;
  }

  function noteAutocomplete(query) {
    reopenIfClosed();
    counts.autocompleteRequests += 1;
    const text = String(query || "").trim().replace(/\s+/g, " ").slice(0, 80);
    if (text) autocompleteQueries.push(text);
    state.current = tracker;
    state.pinnedHeaders = headers();
    state.pinnedUntil = Date.now() + HEADER_TTL_MS;
    armAbandon();
  }

  function notePlaceDetails() {
    reopenIfClosed();
    counts.placeDetailsRequests += 1;
    state.current = tracker;
    armAbandon();
  }

  function applyNonGoogle(kind) {
    reopenIfClosed();
    if (kind === "saved_address") counts.savedAddressUsed = 1;
    if (kind === "gps_location") counts.gpsUsed = 1;
    if (kind === "recent_address") counts.recentAddressUsed = 1;
  }

  function complete() {
    if (closed) return;
    closed = true;
    if (timer) clearTimeout(timer);
    flush(true, false);
    state.pinnedHeaders = headers();
    state.pinnedUntil = Date.now() + HEADER_TTL_MS;
    if (state.current === tracker) state.current = null;
  }

  function abandon() {
    if (closed) return;
    closed = true;
    if (timer) clearTimeout(timer);
    flush(false, false);
    if (state.current === tracker) state.current = null;
  }

  const tracker = {
    searchInteractionId,
    headers,
    noteAutocomplete,
    notePlaceDetails,
    applyNonGoogle,
    complete,
    abandon,
  };
  state.current = tracker;
  return tracker;
}

export function beginAddressUsage(options = {}) {
  const tracker = createInteractionTracker(options);
  state.pinnedHeaders = tracker.headers();
  state.pinnedUntil = Date.now() + HEADER_TTL_MS;
  return {
    finish(kind) {
      tracker.applyNonGoogle(kind);
      tracker.complete();
    },
  };
}

export function recordDynamicMapLoad(feature, actorType = "customer") {
  const now = Date.now();
  if (mapSeen.get(feature) && now - mapSeen.get(feature) < 1500) return;
  mapSeen.set(feature, now);
  postUsage(
    {
      searchInteractionId: randomId(),
      anonymousSessionId: getAnonymousSessionId(),
      feature,
      actorType,
      driveGroupId: null,
      completed: true,
      durationMs: 0,
      autocompleteRequests: 0,
      placeDetailsRequests: 0,
      dynamicMapLoads: 1,
      savedAddressUsed: 0,
      gpsUsed: 0,
      recentAddressUsed: 0,
    },
    false
  );
}

export function resetGoogleUsageForTests() {
  if (state.current && state.current.abandon) {
    const pending = state.current;
    state.current = null;
    pending.abandon();
  }
  state.current = null;
  state.pinnedHeaders = null;
  state.pinnedUntil = 0;
  state.driveGroupId = null;
  transport = null;
  mapSeen.clear();
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (state.current) state.current.abandon();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && state.current) state.current.abandon();
  });
}
